/**
 * @lodex-pro/web-portal/components/PortalDataError — one place that decides how
 * an operator-portal data-page load error is presented.
 *
 * Every page gated by `resolveCanonicalOperatorId` (tickets, ticket-detail,
 * volumes, tanks) can fail two ways that must NOT look the same:
 *
 *   1. A real load failure (500, network, etc.) — a broken page. Generic
 *      "Could not load X." with role="alert".
 *   2. The 412 OPERATOR_PORTAL_NOT_LINKED precondition — the tenant simply
 *      hasn't been linked to its OCC-catalog operator record yet (DEC-153,
 *      `tenants.platform_operator_id IS NULL`). This is an EXPECTED state, not
 *      a failure, so it gets a specific, actionable message with role="status".
 *
 * Centralised here so all four pages stay identical and the distinction is
 * tested in one spot. Branches on the STRUCTURAL `error.code` carried by
 * ApiError — never on the message string (see api-error.ts for why).
 */

import React from 'react';
import { ApiError } from '@lodex-pro/web-shared';

/** Code the API returns (412) when tenants.platform_operator_id IS NULL. */
export const OPERATOR_PORTAL_NOT_LINKED = 'OPERATOR_PORTAL_NOT_LINKED';

export const NOT_LINKED_MESSAGE =
  "Your organization hasn't been linked to its operator record yet. " +
  'Contact your Paelode Systems administrator.';

interface Props {
  /** The query error from useApiQuery (unknown — TanStack types errors as unknown/Error). */
  readonly error: unknown;
  /** Generic message shown for any error that is NOT the not-linked precondition. */
  readonly fallback: string;
}

export function isNotLinkedError(error: unknown): boolean {
  return error instanceof ApiError && error.code === OPERATOR_PORTAL_NOT_LINKED;
}

export function PortalDataError({ error, fallback }: Props): React.JSX.Element {
  if (isNotLinkedError(error)) {
    return (
      <p
        role="status"
        style={{
          padding: '12px 16px',
          background: 'rgba(123, 150, 232, 0.18)',
          color: 'var(--color-accent-blue-dark)',
          borderRadius: '12px',
          fontSize: '14px',
        }}
      >
        {NOT_LINKED_MESSAGE}
      </p>
    );
  }
  return <p role="alert">{fallback}</p>;
}
